// helpers/formEvents.js
//
// READ THE EVENT NOW, NOT LATER.
//
// React 16 POOLS SyntheticEvents: the instant a handler returns, React nulls
// the event's fields and puts the object back in the pool. A functional state
// updater — `setX(prev => ...)` — does NOT run inside the handler. React can
// defer it to the next render, and it does exactly that whenever the component
// already has work queued (the "eager state" shortcut only applies to an idle
// fiber). So this shape:
//
//     onChange={(e) => setAch((p) => ({ ...p, [key]: e.target.value }))}
//
// reads `e.target` after the pool has reclaimed it, and the page dies with
//     TypeError: Cannot read properties of null (reading 'value')
// thrown from inside useState, which is where React runs the queued updater.
//
// It is a genuine intermittent: type into a quiet form and the update is
// computed eagerly, while the event is still alive, and nothing happens. Type
// into one that is already re-rendering and it crashes. Entering the bank name
// on the create-invoice form did it on 14 Aug.
//
// The fix is one line and it is always the same: pull the value out while the
// event is still real, then close over the VALUE. These helpers do that so the
// shape cannot be got wrong again — and so the fix is one import rather than a
// pattern everyone has to remember.
//
// The checkbox equivalent for the brand-autopilot toggles already exists as
// helpers/brandAutopilot#makeAutopilotToggleHandler, from the first time this
// bit us; these are the general forms.

/**
 * onChange for a text/select/date field that lives on a key of an object state.
 *
 *   <input onChange={fieldSetter(setAch, 'achBankName')} />
 *
 * @param {Function} setState  the object state's setter
 * @param {string}   key       which key of it this field writes
 * @param {Function} [map]     optional transform of the raw string
 */
export function fieldSetter(setState, key, map) {
  return (e) => {
    // Captured HERE, while the event is still alive.
    const raw = e && e.target ? e.target.value : '';
    const value = map ? map(raw) : raw;
    setState((prev) => ({ ...(prev || {}), [key]: value }));
  };
}

/** The same, for a checkbox. */
export function checkboxSetter(setState, key) {
  return (e) => {
    const checked = !!(e && e.target && e.target.checked);
    setState((prev) => ({ ...(prev || {}), [key]: checked }));
  };
}

/**
 * onChange for a field inside an ARRAY state — one row of a line-item table.
 *
 *   <input onChange={rowFieldSetter(setLineItems, i, 'quantity')} />
 */
export function rowFieldSetter(setState, index, key, map) {
  return (e) => {
    const raw = e && e.target ? e.target.value : '';
    const value = map ? map(raw) : raw;
    setState((prev) => (prev || []).map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };
}
